import { useEffect, useRef, useState } from "react";
import { useMember } from "../../context/memberContext";
import { useTranslations } from "../../hooks/useTranslations";
import {
  SP,
  getProfitLossesExcel,
  useProfitLosses,
} from "../../http/serviceportalApi";
import {
  calculateCollapsedRows,
  calculateExpandableSums,
  getCollapsedRows,
  getExpandedSums,
  isRowExpandable,
} from "../../selectors/profitLosses";
import { Evaluation, Permission } from "../../types";
import {
  downloadFile,
  getAccountingPeriodUntil,
  getDefaultPeriod,
  getRemoteDataStatus,
  getYearFromPeriod,
  indexed,
} from "../../utils";
import Button from "../ui/Button";
import CompanySelect from "../ui/CompanySelect";
import Table from "../ui/Table";
import ToggleSwitch from "../ui/ToggleSwitch";
import EvaluationSelect from "./EvaluationSelect";
import PeriodSelect from "./PeriodSelect";
import styles from "./ThreeYearAnalysis.module.css";
import ThreeYearAnalysisRow from "./ThreeYearAnalysisRow";

const areAllValuesZero = (
  valueType: SP.ValueType,
  profitLoss: SP.ThreeYearRow,
) => {
  let isValueZero = ([value]: SP.ValuePercent) =>
    valueType === "currency" && value === 0;
  return [profitLoss.range1, profitLoss.range2, profitLoss.range3].every(
    isValueZero,
  );
};

const ThreeYearAnalysis = () => {
  const { i18n } = useTranslations();
  const { member } = useMember();
  const [period, setPeriod] = useState(
    getDefaultPeriod(getAccountingPeriodUntil()),
  );
  const [evaluation, setEvaluation] = useState<Evaluation>("competence");
  const [companyId, setCompanyId] = useState<SP.CompanyId | undefined>(
    undefined,
  );
  const [showAllRows, setShowAllRows] = useState(false);
  const [showWholeYear, setShowWholeYear] = useState(false);
  const [showDifferences, setShowDifferences] = useState(false);
  const [collapsedRows, setCollapsedRows] = useState<Set<number>>();
  const [expandedSums, setExpandedSums] = useState(new Set<number>());
  const [isExcelDownloading, setExcelDownloading] = useState(false);

  const aborter = useRef(new AbortController());
  const { data, isValidating, error } = useProfitLosses(
    member?.id,
    member?.seacNumber,
    companyId,
    period,
    true,
    evaluation === "competence",
    aborter.current.signal,
  );
  const isSuccess = !isValidating && !error;
  const status = getRemoteDataStatus({ isValidating, error });
  const profitLosses = data ?? [];

  useEffect(() => {
    if (!isSuccess) return;
    setCollapsedRows(getCollapsedRows(profitLosses));
  }, [isSuccess]);

  useEffect(() => {
    if (!isSuccess) return;
    if (collapsedRows === undefined) return;

    const collapsed =
      collapsedRows.size > 0
        ? new Set<number>()
        : getCollapsedRows(profitLosses);
    setCollapsedRows(collapsed);

    const expanded =
      collapsed.size > 0 ? new Set<number>() : getExpandedSums(profitLosses);
    setExpandedSums(expanded);
  }, [showAllRows]);

  const cells = (
    <>
      <th
        align="right"
        title={
          i18n.bilanzOnline.threeYearAnalysis.columnHeaders.sum.description
        }
      >
        {i18n.bilanzOnline.threeYearAnalysis.columnHeaders.sum.label}
      </th>
      <th
        align="right"
        title={
          i18n.bilanzOnline.threeYearAnalysis.columnHeaders.percent.description
        }
      >
        {i18n.bilanzOnline.threeYearAnalysis.columnHeaders.percent.label}
      </th>
      {showWholeYear && (
        <>
          <th
            align="right"
            title={
              i18n.bilanzOnline.threeYearAnalysis.columnHeaders.yearAsSum
                .description
            }
          >
            {i18n.bilanzOnline.threeYearAnalysis.columnHeaders.yearAsSum.label}
          </th>
          <th
            align="right"
            title={
              i18n.bilanzOnline.threeYearAnalysis.columnHeaders.yearInPercent
                .description
            }
          >
            {
              i18n.bilanzOnline.threeYearAnalysis.columnHeaders.yearInPercent
                .label
            }
          </th>
        </>
      )}
    </>
  );

  const differenceCell = (
    <th
      align="right"
      title={
        i18n.bilanzOnline.threeYearAnalysis.columnHeaders.difference.description
      }
    >
      {i18n.bilanzOnline.threeYearAnalysis.columnHeaders.difference.label} %
    </th>
  );

  if (!member) return null;

  return (
    <>
      {!member.seacNumber ? (
        <div className={styles.status}>{i18n.configIncomplete}</div>
      ) : (
        <div className={styles.threeYearAnalysis}>
          <div className={styles.filters}>
            <div className={styles.filtersTop}>
              <div className={styles.filterSetting}>
                <label htmlFor="company">
                  {i18n.bilanzOnline.companySelect}
                </label>
                <CompanySelect
                  id="company"
                  className={styles.companySelect}
                  allowedPermissions={[Permission.AccountingsRead]}
                  value={companyId}
                  onChange={(e) =>
                    setCompanyId(
                      e.target.value !== "all"
                        ? parseInt(e.target.value, 10)
                        : undefined,
                    )
                  }
                />
              </div>
              <div className={styles.filterSetting}>
                <label htmlFor="evaluation">
                  {i18n.bilanzOnline.evaluation.label}
                </label>
                <EvaluationSelect id="evaluation" onChange={setEvaluation} />
              </div>
              <div className={styles.filterSetting}>
                <label>{i18n.bilanzOnline.periodSelect.label}</label>
                <PeriodSelect
                  period={period}
                  periodUntil={getAccountingPeriodUntil()}
                  onChange={setPeriod}
                />
              </div>
            </div>
            <div className={styles.filtersBottom}>
              <div className={styles.toggleSwitches}>
                <div className={styles.filterSetting}>
                  <label htmlFor="showAllRows">
                    {i18n.bilanzOnline.threeYearAnalysis.showAllRows}
                  </label>
                  <ToggleSwitch
                    id="showAllRows"
                    checked={showAllRows}
                    onChange={(e) => setShowAllRows(e.target.checked)}
                  />
                </div>
                <div className={styles.filterSetting}>
                  <label htmlFor="showWholeYear">
                    {i18n.bilanzOnline.threeYearAnalysis.showWholeYear}
                  </label>
                  <ToggleSwitch
                    id="showWholeYear"
                    checked={showWholeYear}
                    onChange={(e) => setShowWholeYear(e.target.checked)}
                  />
                </div>
                <div className={styles.filterSetting}>
                  <label htmlFor="showDifferences">
                    {i18n.bilanzOnline.threeYearAnalysis.showDifferences}
                  </label>
                  <ToggleSwitch
                    id="showDifferences"
                    checked={showDifferences}
                    onChange={(e) => setShowDifferences(e.target.checked)}
                  />
                </div>
              </div>
              <div>
                <Button
                  isLoading={isExcelDownloading}
                  buttonProps={{
                    className: styles.downloadButton,
                    disabled: !member.seacNumber,
                    onClick: async () => {
                      if (!member.seacNumber) return;
                      setExcelDownloading(true);
                      const res = await getProfitLossesExcel(
                        member.id,
                        member.seacNumber,
                        companyId,
                        period,
                        true,
                        evaluation === "competence",
                      );
                      await downloadFile(res);
                      setExcelDownloading(false);
                    },
                  }}
                >
                  {i18n.bilanzOnline.threeYearAnalysis.downloadExcel}
                </Button>
              </div>
            </div>
          </div>
          {collapsedRows !== undefined && (
            <Table
              head={
                <>
                  <tr>
                    <th rowSpan={2} style={{ width: "4px", padding: 0 }} />
                    <th rowSpan={2} align="left" className={styles.labelHeader}>
                      {i18n.bilanzOnline.monthlyAnalysis.label}
                    </th>
                    <th colSpan={2 + +showWholeYear * 2 + +showDifferences}>
                      {getYearFromPeriod(0, period)}
                    </th>
                    <th colSpan={2 + +showWholeYear * 2 + +showDifferences}>
                      {getYearFromPeriod(-1, period)}
                    </th>
                    <th colSpan={2 + +showWholeYear * 2}>
                      {getYearFromPeriod(-2, period)}
                    </th>
                  </tr>
                  <tr>
                    {cells}
                    {showDifferences && differenceCell}
                    {cells}
                    {showDifferences && differenceCell}
                    {cells}
                  </tr>
                </>
              }
              body={indexed(profitLosses)
                .filter(
                  ([x, index]) =>
                    !(
                      collapsedRows.has(index) ||
                      areAllValuesZero(x.valueType, x.value)
                    ),
                )
                .map(([x, index]) => (
                  <ThreeYearAnalysisRow
                    key={index}
                    isExpandable={isRowExpandable(index, profitLosses)}
                    isExpanded={expandedSums.has(index)}
                    isCollapsed={collapsedRows.has(index)}
                    expandCollapse={() => {
                      const expanded = calculateExpandableSums(
                        index,
                        profitLosses,
                        expandedSums,
                      );
                      const collapsed = calculateCollapsedRows(
                        index,
                        profitLosses,
                        collapsedRows,
                      );
                      setExpandedSums(expanded);
                      setCollapsedRows(collapsed);
                    }}
                    profitLoss={x}
                    showWholeYear={showWholeYear}
                    showDifferences={showDifferences}
                  />
                ))}
              total={profitLosses.length}
              remoteDataStatus={status}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ThreeYearAnalysis;
